<script setup>
/* global axios */
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import DateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const route = useRoute();
const { t } = useI18n();

const specialClosedDates = ref([]);
const newDateRange = ref([]);
const isLoading = ref(false);

const formatDateForApi = date => {
  return date ? format(date, 'yyyy-MM-dd') : '';
};

const parseDateFromApi = dateStr => {
  return dateStr ? parseISO(dateStr) : null;
};

const fetchSpecialClosedDates = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${route.params.accountId}/special_closing_days`,
      { params: { inbox_id: props.inboxId } }
    );
    specialClosedDates.value = response.data.payload || [];
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const validateDateRange = dateRange => {
  if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
    return t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.VALIDATION_ERROR');
  }
  if (dateRange[0] > dateRange[1]) {
    return t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.END_BEFORE_START');
  }
  return null;
};

const saveNew = async newDates => {
  const validationError = validateDateRange(newDates);
  if (validationError) {
    useAlert(validationError);
    return;
  }

  isLoading.value = true;
  try {
    await axios.post(
      `/api/v1/accounts/${route.params.accountId}/special_closing_days`,
      {
        special_closing_day: {
          inbox_id: props.inboxId,
          closed_from_date: formatDateForApi(newDates[0]),
          closed_until_date: formatDateForApi(newDates[1]),
        },
      }
    );
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.SAVED'));
    await fetchSpecialClosedDates();
    newDateRange.value = [];
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const updateItem = async (item, newDates) => {
  const validationError = validateDateRange(newDates);
  if (validationError) {
    useAlert(validationError);
    return;
  }

  isLoading.value = true;
  try {
    await axios.patch(
      `/api/v1/accounts/${route.params.accountId}/special_closing_days/${item.id}`,
      {
        special_closing_day: {
          closed_from_date: formatDateForApi(newDates[0]),
          closed_until_date: formatDateForApi(newDates[1]),
        },
      }
    );
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.SAVED'));
    await fetchSpecialClosedDates();
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const deleteItem = async id => {
  isLoading.value = true;
  try {
    await axios.delete(
      `/api/v1/accounts/${route.params.accountId}/special_closing_days/${id}`
    );
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.DELETED'));
    await fetchSpecialClosedDates();
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const getItemDateRange = item => {
  return [
    parseDateFromApi(item.closed_from_date),
    parseDateFromApi(item.closed_until_date),
  ];
};

onMounted(() => {
  fetchSpecialClosedDates();
});
</script>

<template>
  <div class="special-closed-dates">
    <div class="flex items-center gap-2 mb-4">
      <h3 class="text-lg font-medium">
        {{ t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.TITLE') }}
      </h3>
      <NextButton
        v-if="newDateRange.length === 0"
        xs
        faded
        :label="t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.ADD')"
        icon="i-lucide-plus"
        @click="newDateRange = [null, null]"
      />
    </div>

    <!-- Add new form -->
    <div v-if="newDateRange.length > 0" class="p-4 mb-4 bg-gray-50 rounded-lg">
      <div class="mb-4">
        <DateRangePicker
          :value="newDateRange"
          :placeholder="
            t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.DATE_RANGE')
          "
          :confirm-text="
            t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.CONFIRM')
          "
          @change="saveNew($event)"
        />
      </div>
    </div>

    <!-- List of special closed dates -->
    <div
      v-if="specialClosedDates.length === 0 && newDateRange.length === 0"
      class="text-gray-500 text-sm"
    >
      {{ t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.EMPTY') }}
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="item in specialClosedDates"
        :key="item.id"
        class="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
      >
        <div class="flex-1">
          <NextButton
            v-if="!isLoading"
            xs
            faded
            ruby
            icon="i-lucide-trash-2"
            @click="deleteItem(item.id)"
          />
          <DateRangePicker
            class="inline"
            :value="getItemDateRange(item)"
            :placeholder="
              t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.DATE_RANGE')
            "
            :confirm-text="
              t('INBOX_MGMT.BUSINESS_HOURS.SPECIAL_CLOSED_DATES.CONFIRM')
            "
            @change="updateItem(item, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.special-closed-dates {
  margin-top: 1.5rem;
}
</style>
